import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try { await axios.post(`${API}/contact`, form); setStatus("success"); setForm({ name: "", email: "", company: "", phone: "", message: "" }); }
    catch { setStatus("error"); setErrorMsg("Something went wrong. Please try again."); }
  };

  const inputClass = "w-full bg-white border border-[#ebe5dc] rounded-xl px-4 py-3 text-sm text-[#2d2926] placeholder-[#b8afa4] focus:border-[#2a9d8f] focus:ring-1 focus:ring-[#2a9d8f] transition-all outline-none";

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 relative warm-section-alt">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#2a9d8f] mb-4">Start Here</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#2d2926] mb-6" style={{ fontFamily: 'Fraunces, serif' }}>
              Book Your Free <span className="gradient-text">Discovery Call</span>
            </h2>
            <p className="text-base text-[#5c564e] leading-relaxed mb-8">
              Not sure where to start? Tell us about your business challenge and we'll recommend the right approach — no obligation, no jargon, just a straight conversation.
            </p>
            <div className="space-y-4">
              {["Free 30-minute discovery call — no commitment", "We'll recommend the right approach for your needs", "Response within 24 hours on business days"].map((t, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-[#2a9d8f] rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-[#5c564e]">{t}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <div className="warm-card p-8">
              {status === "success" ? (
                <div className="flex flex-col items-center text-center py-10" data-testid="contact-success-message">
                  <CheckCircle className="text-[#2a9d8f] w-12 h-12 mb-4" />
                  <h3 className="text-xl font-semibold text-[#2d2926] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>Message Sent!</h3>
                  <p className="text-sm text-[#5c564e]">We'll get back to you within 24 hours.</p>
                  <button onClick={() => setStatus("idle")} data-testid="contact-send-another" className="mt-6 text-sm text-[#2a9d8f] hover:text-[#238b7f]">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" placeholder="Your Name *" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className={inputClass} data-testid="contact-input-name" />
                    <input type="email" placeholder="Email Address *" required value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClass} data-testid="contact-input-email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input type="text" placeholder="Company Name" value={form.company} onChange={e => setForm({...form, company: e.target.value})} className={inputClass} data-testid="contact-input-company" />
                    <input type="tel" placeholder="Phone Number" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className={inputClass} data-testid="contact-input-phone" />
                  </div>
                  <textarea placeholder="Tell us about your project or challenge... *" required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className={`${inputClass} resize-none`} data-testid="contact-input-message" />
                  {errorMsg && <p className="text-sm text-red-500" data-testid="contact-error-message">{errorMsg}</p>}
                  <button type="submit" disabled={status === "loading"} data-testid="contact-submit-button"
                    className="w-full bg-[#2a9d8f] text-white px-8 py-4 rounded-full font-bold hover:bg-[#238b7f] transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={18} /> Send Message</>}
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
